import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import ApiClient from '../../config/ApiClient';
import { useRoleLabels } from '../../hooks/useRoleLabels';
import { AssignmentInvitation } from '../../object-actions/types/types';
import InviteUserForm from './InviteUserForm';
import PendingInvitationsList from './PendingInvitationsList';

interface InviteAthleteModalProps {
    open: boolean;
    assignmentId: number;
    onClose: () => void;
    /** Called after a successful invite so parents can refresh assignment state. */
    onInvited?: (invitation: AssignmentInvitation) => void;
}

/**
 * Modal wrapper around the same InviteUserForm used on Manage Assignment
 * (`/account/payments/:id`). Keeps invite UX consistent across onboarding and
 * the payments manage screen.
 */
const InviteAthleteModal: React.FC<InviteAthleteModalProps> = ({
    open,
    assignmentId,
    onClose,
    onInvited,
}) => {
    const { getRoleLabel } = useRoleLabels();
    const athleteLabel = getRoleLabel('athlete');
    const [pendingInvitations, setPendingInvitations] = useState<AssignmentInvitation[]>([]);
    const [loading, setLoading] = useState(false);

    const refreshInvitations = useCallback(async () => {
        if (!assignmentId) return;
        setLoading(true);
        try {
            const res = await ApiClient.get<AssignmentInvitation[]>(
                `/api/payment-assignments/${assignmentId}/invitations/`,
            );
            if (res.success && Array.isArray(res.data)) {
                setPendingInvitations(res.data);
            }
        } finally {
            setLoading(false);
        }
    }, [assignmentId]);

    useEffect(() => {
        if (!open) return;
        refreshInvitations();
    }, [open, refreshInvitations]);

    const handleInvited = (invitation: AssignmentInvitation) => {
        setPendingInvitations((prev) => [invitation, ...prev]);
        onInvited?.(invitation);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="invite-athlete-title"
            data-testid="invite-athlete-modal"
        >
            <DialogTitle id="invite-athlete-title">
                Invite your {athleteLabel.toLowerCase()}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Send an invitation so they can join this assignment and take the assessment.
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                    No {athleteLabel.toLowerCase()} is attached to this assignment yet.
                </Alert>
                <InviteUserForm
                    assignmentId={assignmentId}
                    role="athlete"
                    onInvited={handleInvited}
                />
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={18} />
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <PendingInvitationsList
                            assignmentId={assignmentId}
                            invitations={pendingInvitations}
                            role="athlete"
                            onChanged={refreshInvitations}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-attr="invite-athlete-modal_done">
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InviteAthleteModal;
